import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Newspaper, X, Copy, Check } from "@phosphor-icons/react";
import {
  fetchIncidents, fetchEvacAlerts, fetchAlerts, fetchNwccBrief, fetchAcresHistoryBulk, fetchBcFires, fetchNifcIncidents,
} from "../lib/api";
import { shareOrigin } from "../lib/shareOrigin";
import InlineShareButton from "./InlineShareButton";

const CUTOFF_MS = 24 * 3600e3;

function overnightGrowth(hist = []) {
  const pts = hist.filter((p) => p.acres != null).sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  if (pts.length < 2) return 0;
  const cutoff = Date.now() - CUTOFF_MS;
  const last = pts[pts.length - 1];
  if (new Date(last.at).getTime() < cutoff) return 0;
  let base = null;
  for (const p of pts) if (new Date(p.at).getTime() <= cutoff) base = p;
  if (!base) base = pts[0];
  return Math.max(0, last.acres - base.acres);
}

const COUNTY_ORDER = [
  ["okanogan", "Okanogan"], ["chelan", "Chelan"], ["kittitas", "Kittitas"],
  ["douglas", "Douglas"], ["grant", "Grant"], ["ferry", "Ferry"],
  ["stevens", "Stevens"], ["pend_oreille", "Pend Oreille"], ["spokane", "Spokane"],
  ["yakima", "Yakima"], ["klickitat", "Klickitat"], ["whatcom", "Whatcom"],
  ["skagit", "Skagit"], ["bcborder", "BC Border (Canada)"],
];

function buildPost({ incidents, bcFires, hist, evacs, wx, brief, containment = {}, variant = "morning" }) {
  const evening = variant === "evening";
  const date = new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const active = incidents.filter((i) => (i.status_label || "") === "ACTIVE");
  const byCounty = new Map();
  for (const i of active) {
    const k = String(i.county || "other").toLowerCase();
    if (!byCounty.has(k)) byCounty.set(k, []);
    byCounty.get(k).push({ ...i, growth: overnightGrowth(hist[i.id]) });
  }
  const bcActive = (bcFires || []).filter((f) => !/out/i.test(f.status || ""));
  const totalActive = active.length + bcActive.length;

  const lines = [
    evening
      ? `🌙 EVENING FIRE UPDATE — ${date} — All 14 Counties We Cover`
      : `🔥 MORNING FIRE UPDATE — ${date} — All 14 Counties We Cover`,
  ];
  if (brief?.available && brief.pl?.current != null) {
    let l = `NW Preparedness Level ${brief.pl.current}`;
    if (!evening && brief.nw_fires != null) l += ` · ${brief.nw_fires} new fires / ${brief.nw_acres} ac in the NW yesterday`;
    lines.push(l);
  }
  if (evening) {
    const countyLabel = Object.fromEntries(COUNTY_ORDER);
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const starts = incidents
      .filter((i) => i.date && new Date(i.date) >= midnight)
      .sort((a, b) => (b.acres || 0) - (a.acres || 0));
    lines.push("");
    if (starts.length) {
      lines.push(`🆕 NEW STARTS TODAY (${starts.length}):`);
      for (const f of starts.slice(0, 12)) {
        let l = `• ${f.name} — ${countyLabel[String(f.county || "").toLowerCase()] || f.county || "—"}`;
        if (f.acres != null) l += ` · ${Math.round(f.acres * 10) / 10} ac`;
        if (f.type && f.type !== "Wildfire") l += ` (${f.type})`;
        lines.push(l);
      }
      if (starts.length > 12) lines.push(`(+${starts.length - 12} more on the live map)`);
    } else {
      lines.push("🆕 NEW STARTS TODAY: none — a quiet day. 🙏");
    }
  }
  lines.push("");
  lines.push(`ACTIVE FIRES (${totalActive}):`);
  const growthWord = evening ? "today" : "overnight";
  const quiet = [];
  for (const [slug, name] of COUNTY_ORDER) {
    const fires = slug === "bcborder"
      ? bcActive.map((f) => ({ name: f.name || f.fire_number, acres: f.size_acres, growth: 0, note: f.fire_of_note }))
      : (byCounty.get(slug) || []);
    if (!fires.length) {
      quiet.push(name);
      continue;
    }
    fires.sort((a, b) => (b.acres || 0) + (b.growth || 0) * 10 - ((a.acres || 0) + (a.growth || 0) * 10));
    lines.push("");
    lines.push(`${slug === "bcborder" ? name.toUpperCase() : `${name.toUpperCase()} COUNTY`} — ${fires.length} fire${fires.length !== 1 ? "s" : ""}:`);
    for (const f of fires.slice(0, 5)) {
      let l = `• ${f.name}`;
      if (f.acres != null) l += ` — ${Math.round(f.acres * 10) / 10} ac`;
      const cont = containment[String(f.name || "").trim().toLowerCase()];
      if (cont != null) l += ` · ${Math.round(cont)}% contained`;
      if (f.growth > 0) l += ` (+${Math.round(f.growth * 10) / 10} ac ${growthWord})`;
      if (f.note) l += " ⚠️ Fire of Note";
      lines.push(l);
    }
    if (fires.length > 5) lines.push(`(+${fires.length - 5} more on the live map)`);
  }
  if (quiet.length) {
    lines.push("");
    lines.push(`✅ Quiet this morning: ${quiet.join(", ")}`);
  }
  lines.push("");
  const l3 = evacs.filter((a) => a.level === 3);
  const l2 = evacs.filter((a) => a.level === 2);
  const l1 = evacs.filter((a) => a.level === 1);
  if (l3.length || l2.length || l1.length) {
    lines.push("EVACUATIONS:");
    l3.forEach((a) => lines.push(`• 🔴 LEVEL 3 GO NOW: ${a.title}`));
    l2.forEach((a) => lines.push(`• 🟠 Level 2 GET SET: ${a.title}`));
    l1.forEach((a) => lines.push(`• 🟢 Level 1 GET READY: ${a.title}`));
  } else {
    lines.push("EVACUATIONS: none active right now.");
  }
  const redFlag = (wx || []).some((a) => /red flag/i.test(a.event || a.headline || ""));
  if (redFlag) {
    lines.push("");
    lines.push("⚠️ RED FLAG WARNING in effect — wind + dry fuels. Be careful out there today.");
  }
  lines.push("");
  lines.push(`Live map, cameras & scanner: ${shareOrigin()}`);
  return lines.join("\n");
}

export async function loadMorningPostText() {
  return loadReportText("morning");
}

export async function loadEveningPostText() {
  return loadReportText("evening");
}

async function loadReportText(variant) {
  const [incidents, bcFires, evacs, wx, brief, hist, nifc] = await Promise.all([
    fetchIncidents({ county: "all" }).catch(() => []),
    fetchBcFires().catch(() => []),
    fetchEvacAlerts({}).catch(() => []),
    fetchAlerts("all").catch(() => []),
    fetchNwccBrief().catch(() => null),
    fetchAcresHistoryBulk(2).catch(() => ({})),
    fetchNifcIncidents({ active_only: true }).catch(() => []),
  ]);
  const containment = {};
  for (const n of Array.isArray(nifc) ? nifc : []) {
    if (n.percent_contained != null && n.name) {
      containment[String(n.name).trim().toLowerCase()] = n.percent_contained;
    }
  }
  return buildPost({
    incidents: incidents || [],
    bcFires: Array.isArray(bcFires) ? bcFires : bcFires?.fires || [],
    hist: hist || {},
    evacs: evacs || [],
    wx: wx || [],
    brief,
    containment,
    variant,
  });
}

export default function MorningPostButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setText(await loadMorningPostText());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const modal = open ? createPortal(
    <div
      data-testid="morning-post-modal"
      className="fixed inset-0 z-[1400] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl max-h-[90vh] bg-tac-bg border border-tac-border rounded-sm shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-tac-border">
          <span className="text-[12px] font-bold uppercase tracking-[0.15em] text-tac-text flex items-center gap-2">
            <Newspaper size={14} weight="fill" className="text-tac-primary" /> Morning Post
          </span>
          <button type="button" data-testid="morning-post-close" onClick={() => setOpen(false)} className="text-tac-dim hover:text-tac-text">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto tac-scroll">
          {loading ? (
            <div className="text-[12px] font-mono-tac text-tac-dim py-8 text-center">Building today's update…</div>
          ) : (
            <textarea
              data-testid="morning-post-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={16}
              className="w-full bg-tac-surface border border-tac-border rounded-sm p-3 text-[13px] leading-relaxed text-tac-text font-mono-tac focus:outline-none focus:border-tac-primary resize-y"
            />
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-tac-border">
          <span className="text-[10px] font-mono-tac text-tac-faint">{text.length} chars · edit freely before posting</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="morning-post-copy"
              onClick={copy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-tac-border text-tac-text hover:border-tac-primary transition-colors"
            >
              {copied ? <Check size={12} weight="bold" className="text-green-400" /> : <Copy size={12} weight="bold" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <InlineShareButton
              testid="morning-post-share"
              url={shareOrigin()}
              title="Morning Fire Update"
              text={text}
              caption={text}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        type="button"
        data-testid="morning-post-btn"
        title="Build today's ready-to-paste morning fire update"
        onClick={() => setOpen(true)}
        className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-tac-border text-tac-dim hover:text-tac-text hover:border-tac-borderBright transition-colors whitespace-nowrap"
      >
        <Newspaper size={13} weight="bold" />
        <span className="hidden min-[1360px]:inline whitespace-nowrap">Morning Post</span>
      </button>
      {modal}
    </>
  );
}
